import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { cn } from '#/lib/utils';
import FloatingSelect from '#/modules/commonComponents/form/select';
import {
  ensureSelectOption,
  formatCustomerAddressSelectLabel,
  formatCustomerContactSelectLabel,
  mapCustomerAddressSelectOptions,
  mapCustomerContactSelectOptions,
} from '#/modules/crmMgmt/customer/helper/customerAddress';
import { SALES_ORDER_ADDRESS_TIP } from '#/modules/crmMgmt/salesOrder/schemas/salesOrderSchema';

/**
 * 出貨／帳單地址與收貨聯絡人（DRAFT 可編）。
 * 契約：三 ID；勿送字串地址／recipient_*。
 */
const SalesOrderShippingSection = ({
  control,
  errors,
  addresses = [],
  contacts = [],
  snapshot = null,
  isDisabled = false,
  noCustomerMessage = '請先選擇客戶',
}) => {
  const billingAddressId = useWatch({
    control,
    name: 'billingAddressId',
    defaultValue: '',
  });
  const shippingAddressId = useWatch({
    control,
    name: 'shippingAddressId',
    defaultValue: '',
  });
  const shippingContactId = useWatch({
    control,
    name: 'shippingContactId',
    defaultValue: '',
  });

  const billingOptions = useMemo(() => {
    const options = mapCustomerAddressSelectOptions(addresses, 'BILLING');
    const withBlank = [{ value: '', label: '（未指定／使用客戶預設）' }, ...options];
    return ensureSelectOption(
      withBlank,
      billingAddressId,
      formatCustomerAddressSelectLabel(snapshot?.billingAddress),
    );
  }, [addresses, billingAddressId, snapshot?.billingAddress]);

  const shippingOptions = useMemo(() => {
    const options = mapCustomerAddressSelectOptions(addresses, 'SHIPPING');
    const withBlank = [{ value: '', label: '（未指定／使用客戶預設）' }, ...options];
    return ensureSelectOption(
      withBlank,
      shippingAddressId,
      formatCustomerAddressSelectLabel(snapshot?.shippingAddress),
    );
  }, [addresses, shippingAddressId, snapshot?.shippingAddress]);

  const contactOptions = useMemo(() => {
    const options = mapCustomerContactSelectOptions(contacts);
    const withBlank = [
      { value: '', label: '（未指定／使用客戶預設）' },
      ...options,
    ];
    return ensureSelectOption(
      withBlank,
      shippingContactId,
      formatCustomerContactSelectLabel(snapshot?.shippingContact),
    );
  }, [contacts, shippingContactId, snapshot?.shippingContact]);

  return (
    <section className="pb-6">
      <div className="form-block">
        <h2 className="form-block__title">地址與聯絡人</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          {SALES_ORDER_ADDRESS_TIP}
        </p>
      </div>
      <div
        className={cn(
          'grid grid-cols-1 gap-3',
          '@4xl/sheet:grid-cols-2',
        )}
      >
        <FloatingSelect
          id="billingAddressId"
          label="帳單地址"
          options={billingOptions}
          control={control}
          errors={errors}
          isDisabled={isDisabled}
          noOptionsMessage={noCustomerMessage}
        />
        <FloatingSelect
          id="shippingAddressId"
          label="收貨地址"
          options={shippingOptions}
          control={control}
          errors={errors}
          isDisabled={isDisabled}
          noOptionsMessage={noCustomerMessage}
        />
        <FloatingSelect
          id="shippingContactId"
          label="收貨聯絡人"
          options={contactOptions}
          control={control}
          errors={errors}
          isDisabled={isDisabled}
          noOptionsMessage={noCustomerMessage}
        />
      </div>
    </section>
  );
};

SalesOrderShippingSection.propTypes = {
  control: PropTypes.object.isRequired,
  errors: PropTypes.object,
  addresses: PropTypes.arrayOf(PropTypes.object),
  contacts: PropTypes.arrayOf(PropTypes.object),
  snapshot: PropTypes.object,
  isDisabled: PropTypes.bool,
  noCustomerMessage: PropTypes.string,
};

export default SalesOrderShippingSection;
